import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';

import { useAuthContext } from '@/context/AuthContext';
import ENDPOINTS from '@/api';
import {
  sendEmailVerificationCode,
  verifyEmailCode,
  type SendEmailCodeRequest,
  type VerifyEmailCodeRequest,
} from '@/api/email';

export interface EmailVerificationStatus {
  email: string | null;
  verified: boolean;
  verifiedAt: string | null;
}

/**
 * Hook for sending email verification code
 */
export function useSendEmailCode() {
  return useMutation({
    mutationFn: (request: SendEmailCodeRequest) =>
      sendEmailVerificationCode(request),
    onSuccess: (response) => {
      if (response.code === 0) {
        toast.success('Verification code sent to your email');
      } else {
        toast.error(response.message || 'Failed to send verification code');
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to send verification code');
    },
  });
}

/**
 * Hook for verifying email code
 */
export function useVerifyEmailCode() {
  return useMutation({
    mutationFn: (request: VerifyEmailCodeRequest) => verifyEmailCode(request),
    onSuccess: (response) => {
      if (response.code === 0) {
        toast.success('Email verified successfully!');
      } else {
        toast.error(response.message || 'Failed to verify code');
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to verify code');
    },
  });
}

/**
 * Hook to fetch email verification status directly from the database
 * This is more reliable than checking social task status
 */
export function useEmailVerificationStatus() {
  const { isAuthenticated } = useAuthContext();

  return useQuery({
    queryKey: ['emailVerificationStatus'],
    queryFn: async (): Promise<EmailVerificationStatus> => {
      const response = await fetch(ENDPOINTS.emailVerificationStatus, {
        credentials: 'include',
      });

      const json = await response.json();

      if (!response.ok || json.code !== 0) {
        throw new Error(json.message || 'Failed to fetch email verification status');
      }

      return json.data;
    },
    enabled: isAuthenticated,
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: true,
  });
}
